import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PackageCheck, Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { PageHeader, usePermission, useTenant } from "@/components/AppShell";
import { PACKING_LABEL } from "@/components/FbaInfo";
import { COUNTRIES, EMPTY, Field, LineRow, ProductPicker, type Line } from "@/components/ManualOrderSheet";
import { Pager } from "@/components/Pager";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, ApiError, type Address, type FbaPacking, type FbaShipmentRow } from "@/lib/api";
import { countryName, fmtNumber, fmtRelative } from "@/lib/format";
import { STATUS } from "./Orders";

import { t, msg } from "@/lib/i18n";
/** Stand einer Sendung in Worten (Etiketten/Kartons/Lager) */
function stage(r: FbaShipmentRow): { label: string; tone: "neutral" | "success" | "warning" | "danger" | "info" } {
  const labels = r.documents.includes("carton_labels") && (!r.fba.palletized || r.documents.includes("pallet_labels"));
  if (["shipped", "partially_shipped", "cancelled", "failed"].includes(r.status)) return STATUS[r.status];
  if (r.fba.packing === "warehouse_packs" && !r.fba.packedAt) return { label: msg("Logiful packt"), tone: "info" };
  if (!labels) return { label: r.fba.packing === "warehouse_packs" ? "Gepackt – Etiketten fehlen" : "Etiketten fehlen", tone: "warning" };
  return { label: msg("Etiketten da – beim Lager"), tone: "success" };
}

/** Pre-FBA: Sendungen an Amazon-Lager */
export function FbaPage() {
  const { tenantId } = useTenant();
  const mayEdit = usePermission()("orders");
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [page, setPage] = useState(1);
  const q = useQuery({ queryKey: ["fba-shipments", tenantId, page], queryFn: () => api.fba.shipments(tenantId, { page, pageSize: 50 }) });
  const d = q.data;
  return (
    <div className="pb-24">
      <PageHeader
        title={t("Pre-FBA")}
        icon={<PackageCheck className="size-5" />}
        description={t("Ware an Amazon-Lager: Logiful beklebt mit FNSKU, die Karton- und Palettenetiketten aus Seller Central kommen hier dazu – oder Logiful packt zuerst und meldet die Kartons.")}
        actions={mayEdit ? <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>{t("Sendung anlegen")}</Button> : undefined}
      />
      <div className="px-4 md:px-8">
        {q.isLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : !d?.items.length ? (
          <Card>
            <EmptyState icon={<PackageCheck className="size-5" />} title={t("Noch keine Sendungen an Amazon")} action={mayEdit ? <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>{t("Erste Sendung anlegen")}</Button> : undefined}>
              {t("Tipp: FNSKU und „bekleben“ am Artikel unter „Amazon FBA“ hinterlegen – Logiful druckt die Etiketten im Lager.")}
            </EmptyState>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="text-[12px] text-fg-subtle bg-surface-2/60">
                  <tr>
                    <th className="text-left font-medium px-4 py-2">{t("Sendung")}</th>
                    <th className="text-left font-medium px-3 py-2">{t("Amazon")}</th>
                    <th className="text-left font-medium px-3 py-2">{t("Ziel")}</th>
                    <th className="text-left font-medium px-3 py-2">{t("Weg")}</th>
                    <th className="text-right font-medium px-3 py-2">{t("Stück")}</th>
                    <th className="text-left font-medium px-4 py-2">{t("Stand")}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((r) => {
                    const st = stage(r);
                    return (
                      <tr key={r.id} onClick={() => navigate(`/orders?open=${r.id}`)} className="border-t border-border hover:bg-surface-2/50 cursor-pointer">
                        <td className="px-4 py-2.5"><div className="font-medium tabular-nums whitespace-nowrap">{r.orderNumber}</div><div className="text-[12px] text-fg-subtle whitespace-nowrap">{fmtRelative(r.placedAt)}</div></td>
                        <td className="px-3 py-2.5 font-mono text-[12.5px] whitespace-nowrap">{r.fba.shipmentId ?? <span className="text-fg-subtle font-sans">{t("noch offen")}</span>}</td>
                        <td className="px-3 py-2.5 font-mono">{r.fba.destinationCode}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">{r.fba.packing === "seller_cartons" ? t("Kartons vom Kunden") : t("Logiful packt")}{r.fba.palletized ? t(" · Palette") : ""}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{fmtNumber(r.units)}</td>
                        <td className="px-4 py-2.5"><Badge tone={st.tone}>{t(st.label)}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager total={d.total} page={d.page} pageSize={d.pageSize} onPage={setPage} />
          </Card>
        )}
      </div>
      <FbaShipmentSheet open={creating} onOpenChange={setCreating} known={d?.items ?? []} onCreated={(id) => navigate(`/orders?open=${id}`)} />
    </div>
  );
}

const AMAZON_ADDRESS: Address = { ...EMPTY, company: "Amazon EU S.à r.l.", lastName: "" };

function FbaShipmentSheet({ open, onOpenChange, known, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; known: FbaShipmentRow[]; onCreated: (id: string) => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const methods = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), enabled: open });
  const activeMethods = methods.data?.warehouses.find((w) => w.active)?.shippingMethods ?? [];
  const [packing, setPacking] = useState<FbaPacking>("seller_cartons");
  const [shipmentId, setShipmentId] = useState("");
  const [referenceId, setReferenceId] = useState("");
  const [code, setCode] = useState("");
  const [addr, setAddr] = useState<Address>(AMAZON_ADDRESS);
  const [palletized, setPalletized] = useState(false);
  const [methodId, setMethodId] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [note, setNote] = useState("");
  const [tried, setTried] = useState(false);
  // schon belieferte Amazon-Lager: Adresse übernehmen
  const knownByCode = useMemo(() => new Map(known.filter((k) => k.destination).map((k) => [k.fba.destinationCode, k.destination!])), [known]);

  useEffect(() => {
    if (!open) return;
    setPacking("seller_cartons"); setShipmentId(""); setReferenceId(""); setCode(""); setAddr(AMAZON_ADDRESS); setPalletized(false); setMethodId(""); setLines([]); setNote(""); setTried(false);
  }, [open]);
  const onCode = (v: string) => {
    const c = v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5);
    setCode(c);
    const k = knownByCode.get(c);
    if (k) setAddr({ ...EMPTY, ...k });
    else if (!addr.street) setAddr((a) => ({ ...a, lastName: c ? `Fulfillment Center ${c}` : "" }));
  };
  const setA = (k: keyof Address, v: string) => setAddr((a) => ({ ...a, [k]: v }));

  const create = useMutation({
    mutationFn: () =>
      api.fba.create(tenantId, {
        ...(shipmentId.trim() ? { shipmentId: shipmentId.trim() } : {}),
        ...(referenceId.trim() ? { referenceId: referenceId.trim() } : {}),
        destinationCode: code,
        destination: addr,
        packing,
        palletized,
        lines: lines.map((l) => ({ productId: l.product.id, quantity: l.quantity })),
        ...(methodId ? { shippingMethodId: methodId } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: (r) => {
      toast.success(`Sendung ${r.orderNumber} angelegt`, { description: packing === "seller_cartons" ? "Jetzt die Etiketten aus Seller Central hochladen – dann geht sie ans Lager." : "Logiful packt und meldet die Kartons." });
      void qc.invalidateQueries({ queryKey: ["fba-shipments", tenantId] });
      onOpenChange(false);
      onCreated(r.id);
    },
    onError: (e) => toast.error("Nicht angelegt", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });

  const errors = {
    shipmentId: packing === "seller_cartons" && !shipmentId.trim() ? "Amazon-Sendungsnummer aus Seller Central" : shipmentId.trim() && !/^FBA[0-9A-Z]{6,12}$/i.test(shipmentId.trim()) ? "Format: FBA… (z. B. FBA15K2ZX9QW)" : undefined,
    code: !/^[A-Z0-9]{3,5}$/.test(code) ? "Kürzel des Amazon-Lagers, z. B. DTM2" : undefined,
    street: !addr.street.trim() ? "Straße fehlt" : undefined,
    zip: !addr.zip.trim() ? "PLZ fehlt" : undefined,
    city: !addr.city.trim() ? "Ort fehlt" : undefined,
    lastName: !addr.lastName.trim() ? "Empfänger fehlt" : undefined,
    lines: !lines.length ? "Mindestens ein Artikel" : undefined,
  };
  const valid = Object.values(errors).every((e) => !e);
  const err = (k: keyof typeof errors) => (tried ? errors[k] : undefined);
  const units = lines.reduce((s, l) => s + l.quantity, 0);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20 animate-fade-in" />
        <Dialog.Content onOpenAutoFocus={(e) => e.preventDefault()} onEscapeKeyDown={(e) => { if ((document.activeElement as HTMLElement | null)?.closest("[data-picker=open]")) e.preventDefault(); }} className="fixed z-50 right-0 top-0 h-full w-[min(680px,100vw)] bg-surface border-l border-border shadow-2xl flex flex-col animate-sheet-in outline-none">
          <div className="px-6 pt-5 pb-4 border-b border-border flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2"><PackageCheck className="size-4 text-fg-subtle" /> {t("Sendung an Amazon anlegen")}</Dialog.Title>
              <Dialog.Description className="text-[13px] text-fg-muted mt-0.5">{t("Artikel mit Bekleben-Pflicht bekommen im Lager ihr FNSKU-Etikett. Etiketten aus Seller Central lädst du danach am Auftrag hoch.")}</Dialog.Description>
            </div>
            <Dialog.Close className="size-8 -mr-2 grid place-items-center rounded-lg text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>
          <div data-sheet-body className="flex-1 overflow-y-auto px-6 py-5 space-y-7">
            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Ablauf")}</h3>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {(["seller_cartons", "warehouse_packs"] as const).map((p) => (
                  <label key={p} className={cx("flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer", packing === p ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60")}>
                    <input type="radio" className="mt-1 accent-ink" checked={packing === p} onChange={() => setPacking(p)} />
                    <span>
                      <span className="block text-[13px] font-medium">{PACKING_LABEL[p]}</span>
                      <span className="block text-[12px] text-fg-muted">
                        {p === "seller_cartons" ? t("Du hast die Sendung samt Kartons in Seller Central angelegt. Sie geht ans Lager, sobald die Etiketten hier hochgeladen sind.") : t("Logiful packt zuerst und meldet Kartons, Gewicht und Maße. Damit legst du die Sendung in Seller Central an und lädst die Etiketten hoch.")}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </section>

            <section className="grid grid-cols-6 gap-x-3 gap-y-2.5">
              <Field label={packing === "seller_cartons" ? t("Amazon-Sendungsnummer") : t("Amazon-Sendungsnummer (später)")} error={err("shipmentId")} className="col-span-6 sm:col-span-3"><Input value={shipmentId} onChange={(e) => setShipmentId(e.target.value.toUpperCase().slice(0, 15))} placeholder={t("FBA15…")} className="[&_input]:font-mono" invalid={!!err("shipmentId")} /></Field>
              <Field label={t("Referenz-ID (optional)")} className="col-span-6 sm:col-span-3"><Input value={referenceId} onChange={(e) => setReferenceId(e.target.value.slice(0, 40))} placeholder={t("für den Spediteur")} className="[&_input]:font-mono" /></Field>
              <label className="col-span-6 flex items-center gap-2 text-[13px]"><input type="checkbox" className="accent-ink" checked={palletized} onChange={(e) => setPalletized(e.target.checked)} /> {t("auf Paletten (Spedition) – dann werden auch Palettenetiketten gebraucht")}</label>
            </section>

            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Ziel-Lager bei Amazon")}</h3>
              <div className="mt-2 grid grid-cols-6 gap-x-3 gap-y-2.5">
                <Field label={t("Kürzel")} error={err("code")} className="col-span-2"><Input value={code} onChange={(e) => onCode(e.target.value)} placeholder={t("DTM2")} list="fba-known-codes" className="[&_input]:font-mono" invalid={!!err("code")} /></Field>
                <datalist id="fba-known-codes">{[...knownByCode.keys()].map((c) => <option key={c} value={c} />)}</datalist>
                <Field label={t("Firma")} className="col-span-4"><Input value={addr.company ?? ""} onChange={(e) => setA("company", e.target.value)} /></Field>
                <Field label={t("Empfänger")} error={err("lastName")} className="col-span-6"><Input value={addr.lastName} onChange={(e) => setA("lastName", e.target.value)} placeholder={t("Fulfillment Center DTM2")} invalid={!!err("lastName")} /></Field>
                <Field label={t("Straße")} error={err("street")} className="col-span-4"><Input value={addr.street} onChange={(e) => setA("street", e.target.value)} invalid={!!err("street")} /></Field>
                <Field label={t("Nr.")} className="col-span-2"><Input value={addr.houseNumber ?? ""} onChange={(e) => setA("houseNumber", e.target.value)} /></Field>
                <Field label={t("PLZ")} error={err("zip")} className="col-span-2"><Input value={addr.zip} onChange={(e) => setA("zip", e.target.value)} invalid={!!err("zip")} /></Field>
                <Field label={t("Ort")} error={err("city")} className="col-span-2"><Input value={addr.city} onChange={(e) => setA("city", e.target.value)} invalid={!!err("city")} /></Field>
                <Field label={t("Land")} className="col-span-2">
                  <select value={addr.country} onChange={(e) => setA("country", e.target.value)} className="h-9 w-full rounded-lg border border-border bg-surface px-2.5 text-[13px]">
                    {COUNTRIES.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
                  </select>
                </Field>
              </div>
              <p className="mt-1.5 text-[12px] text-fg-subtle">{t("Die Adresse steht in Seller Central bei der Sendung. Bereits belieferte Lager werden über das Kürzel vorgeschlagen.")}</p>
            </section>

            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Positionen")}</h3>
              <ProductPicker open={open} exclude={lines.map((l) => l.product.id)} onPick={(p) => setLines((xs) => [...xs, { product: p, quantity: 1 }])} />
              {lines.length > 0 && (
                <ul className="mt-2 rounded-xl border border-border divide-y divide-border">
                  {lines.map((l) => <LineRow key={l.product.id} line={l} onQty={(q) => setLines((xs) => xs.map((x) => (x.product.id === l.product.id ? { ...x, quantity: q } : x)))} onRemove={() => setLines((xs) => xs.filter((x) => x.product.id !== l.product.id))} />)}
                </ul>
              )}
              {err("lines") && <p className="mt-1 text-[11.5px] text-danger">{err("lines")}</p>}
            </section>

            <section className="grid grid-cols-6 gap-x-3 gap-y-2.5">
              <Field label={t("Versandart im Lager")} className="col-span-6">
                <select value={methodId} onChange={(e) => setMethodId(e.target.value)} className="h-9 w-full rounded-lg border border-border bg-surface px-2.5 text-[13px]">
                  <option value="">{t("Automatisch (nach Versandregeln)")}</option>
                  {activeMethods.map((m) => <option key={m.id} value={m.id}>{m.name}{m.carrier ? ` (${m.carrier})` : ""}</option>)}
                </select>
              </Field>
              <Field label={t("Hinweis fürs Lager (optional)")} className="col-span-6"><textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} rows={2} className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] outline-none focus:border-border-strong" /></Field>
            </section>
          </div>
          <div className="px-6 py-3.5 border-t border-border flex items-center gap-3 bg-surface">
            <div className="min-w-0 flex-1 text-[12.5px] text-fg-muted">
              {lines.length ? <><b className="text-fg">{lines.length}</b> {lines.length === 1 ? t("Position") : t("Positionen")} · {fmtNumber(units)} {t("Stück")}</> : t("Noch keine Artikel")}
              {tried && !valid && <span className="block text-danger">{t("Bitte die markierten Felder ausfüllen.")}</span>}
            </div>
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" loading={create.isPending} onClick={() => { setTried(true); if (valid) create.mutate(); }}>{t("Sendung anlegen")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

